import { InTopic } from './in/InTopic';
import { LoggingService } from '@instinet-analytics/core-lib';
import { Identity } from './out/Identity';
import { OutTopic } from './out/OutTopic';
import { Listener } from './out/Listener';
import { OutMessage } from './out/OutMessage';
import { InMessagesProcessor } from './in/InMessagesProcessor';
import { OutMessagesProcessor } from './out/OutMessagesProcessor';
import { Store } from 'redux';
import { SubscriberInfo } from './out/Subscription';
import { embeddedAppStateActions } from 'embedded/store/embeddedAppStateSlice.ts';

export interface EmbeddedApiServiceOptions {
    loggingService: LoggingService;
}

export class EmbeddedApiService {
    private readonly inMessagesProcessor: InMessagesProcessor;
    private readonly outMessagesProcessor: OutMessagesProcessor;
    private store: Store | undefined;

    public constructor(options: EmbeddedApiServiceOptions) {
        this.inMessagesProcessor = new InMessagesProcessor(options);
        this.outMessagesProcessor = new OutMessagesProcessor(options);
    }

    public receiveMessage<TPayload>(
        topic: InTopic,
        payload: TPayload
    ): Promise<string> {
        return this.inMessagesProcessor.receiveMessage(topic, payload);
    }

    public async subscribe<TPayload>(
        identity: Identity,
        topic: OutTopic,
        listener: Listener<TPayload>
    ): Promise<string> {
        return this.outMessagesProcessor.subscribe(identity, topic, listener);
    }

    public hasSubscriber(topic: OutTopic): boolean {
        return this.outMessagesProcessor.hasSubscriber(topic);
    }

    public unsubscribe(identity: Identity, topic: OutTopic): Promise<string> {
        return this.outMessagesProcessor.unsubscribe(identity, topic);
    }

    public send<T extends OutTopic, TPayload>(
        message: OutMessage<T, TPayload>
    ) {
        return this.outMessagesProcessor.publish(message);
    }

    public processPendingAuthenticationMessages() {
        return this.inMessagesProcessor.processPendingAuthenticationMessages();
    }

    public setStore(store: Store) {
        this.store = store;
        this.inMessagesProcessor.store = store;
        this.outMessagesProcessor.store = store;
    }

    public getSubscribersInfo(): SubscriberInfo[] {
        return this.outMessagesProcessor.getSubscribersInfo();
    }

    public setStylesheetUrl(stylesheetUrl: string) {
        if (!this.store) {
            throw new Error('Store not set');
        }
        this.store.dispatch(
            embeddedAppStateActions.setStylesheetUrl({ stylesheetUrl })
        );
    }

    public hideWindowButtons() {
        if (!this.store) {
            throw new Error('Store not set');
        }
        this.store.dispatch(embeddedAppStateActions.hideWindowButtons());
    }

    public setHostBorderPx(hostBorderPx: number) {
        if (!this.store) {
            throw new Error('Store not set');
        }
        this.store.dispatch(
            embeddedAppStateActions.setHostBorderPx({ hostBorderPx })
        );
    }
}
